import { Box, Card, Container, Divider, Typography, Avatar, Chip, Dialog } from '@mui/material';
import { BusinessOutlined, FmdGoodOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { Brand_Enum, Business_Profile_Enum, User_Role_Enum } from '@kheops/graphql';
import { RoutePaths } from '../routes/AppRoutes';
import { PageWrapper } from '../common/components/PageWrapper';
import { currentContextAtom, userAtom } from '../state';
import PhotosUtils from '../common/utils/photos.utils';
import TopAppBar from '../common/components/TopAppBar';
import { useCompanyTeamRoleWording } from '../company/team/useCompanyTeamRoleWording';
import CardWithChipsList from '../common/components/CardWithChipsList';
import CompanyLogo from '../common/components/CompanyLogo';
import useOpenable from '../hooks/useOpenable';
import { brandsAtom } from '../brands/state';
import { administrativeAreasAtom } from '../administrativeAreas/state';
import AdministrativeAreasSettings from './settingsDialog/AdministrativeAreasSettings';
import BrandsSettings from './settingsDialog/BrandsSettings';
import UserDepartments from './departments/UserDepartments';
import ContactInformationCard from './contactInformation/ContactInformationCard';

export default function UserPage(): React.JSX.Element {
  const { t } = useTranslation(['common, settings, discovery, company']);
  const user = useAtomValue(userAtom);
  const { realm, roleName, companyBrand, isCompanyAdmin, userBrands, userAdministrativeAreas } = useAtomValue(currentContextAtom);
  const resolvedWording = useCompanyTeamRoleWording(companyBrand || undefined);
  const isSSROrSSA = [User_Role_Enum.SupplierSalesAdmin, User_Role_Enum.SupplierSalesRep].includes(roleName);
  const brands = useAtomValue(brandsAtom);
  const administrativeAreas = useAtomValue(administrativeAreasAtom);

  const { open: openBrandsDialog, close: closeBrandsDialog, isOpen: isBrandsDialogOpen } = useOpenable();
  const { open: openAreasDialog, close: closeAreasDialog, isOpen: isAreasDialogOpen } = useOpenable();
  const hasAllBrands = userBrands && userBrands[0]?.has_all_brands;
  const hasAllAreas = userAdministrativeAreas && userAdministrativeAreas[0]?.has_all_areas;

  const brandsMap = useMemo(() => {
    if (!brands.length) {
      return {};
    }

    return Object.fromEntries(brands.map((brand) => [brand.value, brand.comment]));
  }, [brands]);

  const userBrandsItems = useMemo(() => {
    if (hasAllBrands) {
      return brands?.map((brand) => {
        return {
          label: brand.comment!,
          icon: <CompanyLogo size="small" variant="transparent" brand={brand.value as Brand_Enum} sx={{ width: 24 }} />
          ,
        };
      });
    }

    return userBrands?.map((brand) => {
      return {
        label: brandsMap[brand.brand!]!,
        icon: <CompanyLogo size="small" variant="transparent" brand={brand.brand} sx={{ width: 24 }} />
        ,
      };
    });
  }, [brandsMap, userBrands]);

  const currentBrands = useMemo(() => {
    if (hasAllBrands) {
      return brands;
    }

    const brandValues = userBrands?.map(({ brand }) => brand) ?? [];
    return brands?.filter((brand) => brandValues.includes(brand.value as Brand_Enum)) ?? [];
  }, [brands, userBrands]);

  const userAdministrativeAreasItems = useMemo(() => {
    if (hasAllAreas) {
      return administrativeAreas.map(({ area }) => {
        return {
          label: area,
        };
      });
    }

    return userAdministrativeAreas?.map(({ area }) => {
      return {
        label: area!,
      };
    });
  }, [administrativeAreas, userAdministrativeAreas]);

  const currentAdministrativeAreas = useMemo(() => {
    if (hasAllAreas) {
      return administrativeAreas;
    }

    const administrativeAreasValues = userAdministrativeAreas?.map(({ area }) => area) ?? [];
    return administrativeAreas?.filter((administrative_area) => administrativeAreasValues.includes(administrative_area.area)) ?? [];
  }, [administrativeAreas, userAdministrativeAreas]);

  return (
    <>
      <TopAppBar
        configuration="small"
        backTo={RoutePaths.NAVIGATION_MAIN}
        shouldCheckHistory={false}
        title={t('common:personal_information')}
      />
      <PageWrapper
        sx={{
          pt: {
            xs: 0.5,
            lg: 2,
          },
        }}
      >
        <Container
          fixed
          disableGutters
        >
          <Card
            sx={{
              maxWidth: 640,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mx: 'auto',
              mt: 0,
              mb: {
                xs: 10,
                lg: 0,
              },
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Avatar src={PhotosUtils.getUserAvatarPicture(user.avatars)} size={112} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="headlineSmall">
                  {user.first_name} {user.last_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {isCompanyAdmin && <Chip label={t('company:admin')} variant="tag" color="tertiaryContainer" />}
                  <Chip label={t(resolvedWording[roleName])} variant="tag" color="surfaceContainerHigh" />
                </Box>
              </Box>
            </Box>
            {realm === Business_Profile_Enum.Buyer && roleName === User_Role_Enum.BuyerManager && <UserDepartments />}

            {isSSROrSSA && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="titleLarge">
                      {t('settings:your_scope')}
                    </Typography>
                    <Typography variant="bodySmall" color="onSurfaceVariant">
                      {t('settings:your_scope_description')}
                    </Typography>
                  </Box>

                  <CardWithChipsList
                    titleIcon={<BusinessOutlined />}
                    title={t('settings:brands')}
                    emptyLabel={t('settings:no_brands')}
                    allItemsLabel={t('settings:all_brands')}
                    hasAllItems={!!hasAllBrands}
                    items={userBrandsItems}
                    onEdit={openBrandsDialog}
                  />
                  <Dialog
                    open={isBrandsDialogOpen}
                    sx={{
                      '& .MuiPaper-root.MuiDialog-paper': {
                        width: {
                          xs: '100%',
                          sm: 640,
                        },
                        height: {
                          sm: 560,
                        },
                      },
                    }}
                  >
                    <BrandsSettings
                      next={closeBrandsDialog}
                      brands={brands!}
                      currentBrands={currentBrands}
                      standAlone
                    />
                  </Dialog>
                  <CardWithChipsList
                    titleIcon={<FmdGoodOutlined />}
                    title={t('discovery:administrative_area_level_2')}
                    emptyLabel={t('settings:no_administrative_area_level_2')}
                    items={userAdministrativeAreasItems}
                    allItemsLabel={t('settings:all_administrative_area_level_2')}
                    hasAllItems={!!hasAllAreas}
                    onEdit={openAreasDialog}
                  />
                  <Dialog
                    open={isAreasDialogOpen}
                    sx={{
                      '& .MuiPaper-root.MuiDialog-paper': {
                        width: {
                          xs: '100%',
                          sm: 640,
                        },
                        height: {
                          sm: 560,
                        },
                      },
                    }}
                  >
                    <AdministrativeAreasSettings
                      next={closeAreasDialog}
                      areas={administrativeAreas}
                      currentAreas={currentAdministrativeAreas}
                      standAlone
                    />
                  </Dialog>
                </Box>
                <Divider />
              </>
            )}

            {realm === Business_Profile_Enum.Buyer && <Divider />}
            <ContactInformationCard />
          </Card>
        </Container>
      </PageWrapper>
    </>
  );
}
